/**
 * Console filter to suppress known third-party storage access warnings
 * These warnings are expected when Tracking Prevention is enabled and
 * third-party scripts (AdSense, Analytics) try to access storage.
 */

/**
 * Filter out known Tracking Prevention warnings from console
 * This helps reduce console noise while preserving important errors
 */
export const setupConsoleFilter = (): (() => void) => {
  if (typeof window === 'undefined') return () => {};

  // Store original console methods
  const originalWarn = console.warn;
  const originalError = console.error;

  // Patterns to filter (these are expected warnings from third-party scripts)
  const filterPatterns = [
    /Tracking Prevention blocked access to storage/i,
    /Tracking Prevention blocked access to storage for/i,
    // Vercel Speed Insights errors
    /Vercel Speed Insights/i,
    /speed-insights/i,
    /_vercel\/speed-insights/i,
    /Failed to load script from.*speed-insights/i,
    /vercel\.com\/_vercel\/speed-insights/i,
    /vercel\.com\/api\/jwt.*403/i,
    /ERR_NAME_NOT_RESOLVED/i,
    // Google sync errors (third-party tracking)
    /sync\?ssp=google/i,
    /google_gid/i,
    /google_cver/i,
    /google_push/i,
    /Slow network is detected/i,
    /Slow network is detected.*chromestatus/i,
    /Slow network is detected.*Fallback font/i,
    /Fallback font will be used/i,
    /Fallback font will be used while loading/i,
    /was preloaded using link preload but not used/i,
    /\[Intervention\]/i,
    /\[Intervention\].*Slow network/i,
    /\[Intervention\] Slow network/i,
    /\[Intervention\].*Fallback font/i,
    /\[Intervention\] Images loaded lazily/i,
    /\[Intervention\].*Images loaded lazily/i,
    /\[Intervention\].*Load events are deferred/i,
    /\[Intervention\].*Images loaded lazily and replaced with placeholders/i,
    /Images loaded lazily and replaced with placeholders/i,
    /Load events are deferred/i,
    /See https:\/\/go\.microsoft\.com\/fwlink\/\?linkid=2048113/i,
    /The resource.*was preloaded using link preload/i,
    /was preloaded using link preload but not used within/i,
    /Please make sure it has an appropriate.*as.*value/i,
    /Failed to load resource.*401/i,
    /the server responded with a status of 401/i,
    /health.*401/i,
    /api\/ats\/health.*401/i,
    /motivamatchbackend.*health.*401/i,
    // Filter 429 rate limit errors
    /Failed to load resource.*429/i,
    /the server responded with a status of 429/i,
    /429 \(\)/i,
    // Filter 429 errors from specific endpoints
    /cached-company-matches.*429/i,
    /cached-company-matches.*:\d+.*429/i,
    /cached-company-matches.*:\d+.*Failed to load resource.*429/i,
    /auth\/status.*429/i,
    /auth\/status.*:\d+.*429/i,
    /auth\/status.*:\d+.*Failed to load resource.*429/i,
    /motivamatchbackend.*auth\/status.*429/i,
    /railway\.app.*auth\/status.*429/i,
    /ats\/saved-ats-data.*429/i,
    /ats\/saved-ats-data.*:\d+.*429/i,
    /ats\/saved-ats-data.*:\d+.*Failed to load resource.*429/i,
    /motivamatchbackend.*saved-ats-data.*429/i,
    /railway\.app.*saved-ats-data.*429/i,
    /ats\/profile-boost\/results.*429/i,
    /ats\/profile-boost\/results.*:\d+.*429/i,
    /ats\/profile-boost\/results.*:\d+.*Failed to load resource.*429/i,
    /motivamatchbackend.*profile-boost.*429/i,
    /railway\.app.*profile-boost.*429/i,
    /ats\/interview-prep\/list.*429/i,
    /ats\/interview-prep\/list.*:\d+.*429/i,
    /ats\/interview-prep\/list.*:\d+.*Failed to load resource.*429/i,
    /motivamatchbackend.*interview-prep.*429/i,
    /railway\.app.*interview-prep.*429/i,
    // Filter 401 errors from specific endpoints
    /cached-company-matches.*401/i,
    /cached-company-matches.*:\d+.*401/i,
    /cached-company-matches.*:\d+.*Failed to load resource.*401/i,
    /ats\/cvs.*401/i,
    /ats\/cvs.*:\d+.*401/i,
    /ats\/cvs.*:\d+.*Failed to load resource.*401/i,
    /motivamatchbackend.*ats\/cvs.*401/i,
    /railway\.app.*ats\/cvs.*401/i,
    /ats\/upload-limits.*401/i,
    /ats\/upload-limits.*:\d+.*401/i,
    /ats\/upload-limits.*:\d+.*Failed to load resource.*401/i,
    /motivamatchbackend.*upload-limits.*401/i,
    // Filter non-critical response parsing errors (handled gracefully)
    /Response parsing error/i,
    /Failed to parse server response/i,
    /profile\/data.*Response parsing/i,
    /railway\.app.*upload-limits.*401/i,
    /ats\/interview-prep\/list.*401/i,
    /ats\/interview-prep\/list.*:\d+.*401/i,
    /ats\/interview-prep\/list.*:\d+.*Failed to load resource.*401/i,
    /motivamatchbackend.*interview-prep.*401/i,
    /railway\.app.*interview-prep.*401/i,
    /ats\/saved-ats-data.*401/i,
    /ats\/saved-ats-data.*:\d+.*401/i,
    /ats\/saved-ats-data.*:\d+.*Failed to load resource.*401/i,
    /motivamatchbackend.*saved-ats-data.*401/i,
    /railway\.app.*saved-ats-data.*401/i,
    /ats\/profile-boost\/results.*401/i,
    /ats\/profile-boost\/results.*:\d+.*401/i,
    /ats\/profile-boost\/results.*:\d+.*Failed to load resource.*401/i,
    /motivamatchbackend.*profile-boost.*401/i,
    /railway\.app.*profile-boost.*401/i,
    /auth\/status.*401/i,
    /auth\/status.*:\d+.*401/i,
    /auth\/status.*:\d+.*Failed to load resource.*401/i,
    /motivamatchbackend.*auth\/status.*401/i,
    /railway\.app.*auth\/status.*401/i,
    // Filter 2FA verify 400 errors (expected when 2FA is not set up)
    /api\/auth\/2fa\/verify.*400/i,
    /auth\/2fa\/verify.*400/i,
    /auth\/2fa\/verify.*:\d+.*400/i,
    /auth\/2fa\/verify.*:\d+.*Failed to load resource.*400/i,
    /auth\/2fa\/verify.*Failed to load/i,
    /auth\/2fa\/verify.*the server responded with a status of 400/i,
    /2fa\/verify.*400/i,
    /motivamatchbackend.*2fa\/verify.*400/i,
    /railway\.app.*2fa\/verify.*400/i,
    /motivamatchbackend.*auth\/2fa\/verify.*:\d+.*400/i,
    /railway\.app.*auth\/2fa\/verify.*:\d+.*400/i,
    // Filter generic 404 errors (expected for missing resources)
    /Failed to load resource.*the server responded with a status of 404/i,
    /:\d+.*Failed to load resource.*the server responded with a status of 404/i,
    /motivamatchbackend.*:\d+.*Failed to load resource.*404/i,
    /railway\.app.*:\d+.*Failed to load resource.*404/i,
    /ERR_NAME_NOT_RESOLVED/i,
    /net::ERR_NAME_NOT_RESOLVED/i,
    /Failed to load resource.*net::ERR_NAME_NOT_RESOLVED/i,
    /sync\?ssp=google/i,
    /sync\?ssp=google.*google_gid/i,
    /googlesyndication\.com.*sync/i,
    /Failed to load resource.*googlesyndication/i,
    /Failed to load resource.*doubleclick/i,
    /Failed to load resource.*google.*sync/i,
    /Failed to load resource.*googleads\.g\.doubleclick/i,
    /googleads\.g\.doubleclick\.net.*ERR_NAME_NOT_RESOLVED/i,
    /interaction\/\?ai=.*ERR_NAME_NOT_RESOLVED/i,
    /Failed to load resource.*interaction\/\?ai=/i,
    /Failed to load resource.*\/interaction\//i,
    /Failed to load resource.*mediago\.io/i,
    /Failed to load resource.*dotomi\.com/i,
    /Failed to load resource.*adform\.net/i,
    /Failed to load resource.*casalemedia\.com/i,
    /Failed to load resource.*simpli\.fi/i,
    /Failed to load resource.*appier\.net/i,
    /Failed to load resource.*bidswitch\.net/i,
    /Failed to load resource.*contextweb\.com/i,
    /Failed to load resource.*creativecdn\.com/i,
    /Failed to load resource.*woff2/i,
    /Failed to load resource.*\.woff/i,
    /fonts\.gstatic\.com.*woff/i,
    /fonts\.gstatic\.com.*woff2/i,
    /UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiJ-Ek-_EeA\.woff2/i,
    /Failed to load resource.*UcCO3FwrK3iLTeHuS/i,
    /site\.webmanifest.*404/i,
    /site\.webmanifest.*:\d+.*Failed to load resource.*404/i,
    /Failed to load resource.*site\.webmanifest/i,
    /Manifest fetch.*site\.webmanifest.*failed/i,
    /Manifest fetch from.*site\.webmanifest.*failed/i,
    /Manifest fetch.*code 404/i,
    /Manifest fetch from.*motivamatch.*site\.webmanifest.*failed/i,
    /Manifest fetch from.*motivamatch.*site\.webmanifest.*code 404/i,
    /Manifest fetch from.*https.*site\.webmanifest.*failed/i,
    /Manifest fetch from.*https.*site\.webmanifest.*code 404/i,
    // Filter payment status 404 errors (endpoint not implemented yet)
    /api\/payment\/status.*404/i,
    /payment\/status.*Failed to load/i,
    /payment\/status.*the server responded with a status of 404/i,
    // Filter pricing route 404 errors (should be fixed now, but filter just in case)
    /pricing:1.*Failed to load resource/i,
    /pricing.*404/i,
    /Failed to load resource.*pricing/i,
    /manifest fetch.*failed/i,
    /manifest.*code 404/i,
    /Failed to load resource.*the server responded with a status of 404/i,
    /responded with a status of 404.*\(\)/i,
    /\[AdFooter\].*All checks passed/i,
    /\[AdFooter\].*loading ad/i,
    /\[AdFooter\].*✅/i,
    /net::ERR_ABORTED 404/i,
    /ERR_ABORTED.*404/i,
    /GET.*fonts\.gstatic\.com.*woff2.*404/i,
    /GET.*fonts\.gstatic\.com.*woff2.*ERR_ABORTED/i,
    /UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiJ-Ek-_EeA\.woff2.*Failed/i,
    /UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiJ-Ek-_EeA\.woff2.*404/i,
    /UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiJ-Ek-_EeA\.woff2.*ERR_ABORTED/i,
  ];

  // Helper function to check if message should be filtered
  function shouldFilterMessage(message: string): boolean {
    if (!message) return false;
    
    // PRIORITY: Filter "Node cannot be found" errors FIRST (before any other checks)
    if (message.includes('Node cannot be found') || 
        message.includes('cannot be found in the current page') ||
        message.includes('Node cannot be found in the current page') ||
        message.trim() === 'Node cannot be found in the current page.' ||
        message.trim() === 'Node cannot be found in the current page' ||
        message.includes('cannot be found in the current page.')) {
      return true;
    }
    
    // PRIORITY: Filter Intervention messages (browser performance warnings)
    if (message.includes('[Intervention]') || 
        message.includes('Slow network is detected') ||
        message.includes('Fallback font will be used')) {
      return true;
    }
    
    // PRIORITY: Filter 429 rate limit errors (expected)
    if (message.includes('status of 429') || 
        message.includes('429 ()') ||
        (message.includes('Failed to load resource') && message.includes('429'))) {
      return true;
    }
    
    // Filter 429 errors from specific endpoints (including :1 format)
    if (message.includes('429') && (
        message.includes('cached-company-matches') ||
        message.includes('ats/health') ||
        message.includes('auth/status') ||
        message.includes('ats/cvs') ||
        message.includes('ats/saved-ats-data') ||
        message.includes('saved-ats-data') ||
        message.includes('ats/profile-boost/results') ||
        message.includes('profile-boost') ||
        message.includes('ats/interview-prep/list') ||
        message.includes('interview-prep') ||
        message.includes('ats/profile/data'))) {
      return true;
    }
    
    // PRIORITY: Filter 401 errors from data endpoints (expected when token expires)
    if ((message.includes('status of 401') || message.includes('401 ()')) &&
        (message.includes('data/') || message.includes('/data') || message.includes('Failed to load resource'))) {
      return true;
    }
    
    // Filter 401 errors from specific endpoints (including :1 format)
    if (message.includes('401') && (
        message.includes('cached-company-matches') ||
        message.includes('ats/health') ||
        message.includes('auth/status') ||
        message.includes('ats/cvs') ||
        message.includes('ats/profile/data'))) {
      return true;
    }
    
    // Filter 401 errors from specific endpoints (including full domain)
    if (message.includes('401') && (
        message.includes('ats/health') ||
        message.includes('ats/cvs') ||
        message.includes('ats/upload-limits') ||
        message.includes('upload-limits') ||
        message.includes('ats/interview-prep/list') ||
        message.includes('interview-prep') ||
        message.includes('ats/saved-ats-data') ||
        message.includes('saved-ats-data') ||
        message.includes('ats/profile-boost/results') ||
        message.includes('profile-boost') ||
        message.includes('ats/profile/data') ||
        (message.includes('motivamatchbackend') && (message.includes('401') || message.includes('429'))) ||
        (message.includes('railway.app') && (message.includes('401') || message.includes('429'))))) {
      return true;
    }
    
    // Filter ProfileBoost debug logs
    if (message.includes('ProfileBoost:') || 
        message.includes('🔄 ProfileBoost') ||
        message.includes('✅ ProfileBoost') ||
        message.includes('📋 ProfileBoost') ||
        message.includes('📊 ProfileBoost') ||
        message.includes('⚠️ ProfileBoost') ||
        message.includes('Generate ProfileBoost') ||
        message.includes('transformApiDataToCareerRoles') ||
        message.includes('recommended_job_titles')) {
      return true;
    }
    
    // Filter CVList console errors
    if (message.includes('CVList - API Error')) {
      return true;
    }
    
    // Filter InterviewPrepNew debug logs
    if (message.includes('InterviewPrepNew:') ||
        message.includes('Loaded industry from CV')) {
      return true;
    }
    
    // Filter React DevTools informational message
    if (message.includes('Download the React DevTools') ||
        message.includes('reactjs.org/link/react-devtools') ||
        message.includes('React DevTools')) {
      return true;
    }
    
    // Check against filter patterns
    if (filterPatterns.some(pattern => pattern.test(message))) {
      return true;
    }
    
    // Filter all Intervention messages (Edge browser feature)
    if (/\[Intervention\]/i.test(message)) {
      return true;
    }
    
    // Filter preload warnings (any URL)
    if (/was preloaded using link preload/i.test(message) || 
        /preload but not used within/i.test(message) ||
        /Please make sure it has an appropriate.*as.*value/i.test(message)) {
      return true;
    }
    
    // Filter Tracking Prevention messages (any URL)
    if (/Tracking Prevention/i.test(message)) {
      return true;
    }
    
    // Filter Google-related storage errors
    if (message.includes('storage') && (
      message.includes('googlesyndication.com') ||
      message.includes('google-analytics.com') ||
      message.includes('googletagmanager.com') ||
      message.includes('doubleclick.net') ||
      message.includes('google.com') ||
      message.includes('adservice.google') ||
      message.includes('googleads.g.doubleclick.net')
    )) {
      return true;
    }
    
    // Filter third-party ad/tracking domain storage errors
    if (message.includes('storage') && (
      message.includes('mediago.io') ||
      message.includes('dotomi.com') ||
      message.includes('adform.net') ||
      message.includes('casalemedia.com') ||
      message.includes('simpli.fi') ||
      message.includes('appier.net') ||
      message.includes('bidswitch.net') ||
      message.includes('contextweb.com') ||
      message.includes('creativecdn.com')
    )) {
      return true;
    }
    
    // Filter Google sync errors (ERR_NAME_NOT_RESOLVED) - filter ALL AdSense errors
    // All AdSense errors are expected with ad blockers or network issues
    if (message.includes('ERR_NAME_NOT_RESOLVED') || 
        (message.includes('Failed to load resource') && (
          message.includes('sync') ||
          message.includes('doubleclick') ||
          message.includes('google.com') ||
          message.includes('googleads.g.doubleclick') ||
          message.includes('interaction/?ai=') ||
          message.includes('/interaction/') ||
          message.includes('ssp=google') ||
          message.includes('google_gid') ||
          message.includes('google_cver') ||
          message.includes('mediago.io') ||
          message.includes('dotomi.com') ||
          message.includes('adform.net') ||
          message.includes('casalemedia.com') ||
          message.includes('simpli.fi') ||
          message.includes('appier.net') ||
          message.includes('bidswitch.net') ||
          message.includes('contextweb.com') ||
          message.includes('creativecdn.com') ||
          // Filter ALL googlesyndication errors (including adsbygoogle.js - expected with ad blockers)
          message.includes('googlesyndication') ||
          message.includes('pagead2.googlesyndication.com') ||
          message.includes('adsbygoogle.js')
        ))) {
      return true;
    }
    
    // Filter Google Ads interaction errors specifically
    if ((message.includes('ERR_NAME_NOT_RESOLVED') || message.includes('Failed to load resource')) &&
        (message.includes('interaction') && message.includes('ai='))) {
      return true;
    }
    
    // Filter health check 401 errors (expected when not authenticated)
    if ((message.includes('Failed to load resource') || message.includes('responded with a status of 401')) &&
        (message.includes('health') || message.includes('api/ats/health') || message.includes('motivamatchbackend'))) {
      return true;
    }
    
    // Filter font loading errors (404s are expected - browser will use fallback)
    if ((message.includes('Failed to load resource') || message.includes('responded with a status of 404')) &&
        (message.includes('.woff') || message.includes('.woff2') || message.includes('fonts.gstatic.com') || 
         message.includes('UcCO3FwrK3iLTeHuS') || message.includes('font'))) {
      return true;
    }
    
    // Filter manifest 404 errors (expected - we handle with fallback)
    if ((message.includes('Failed to load resource') || message.includes('responded with a status of 404')) &&
        (message.includes('site.webmanifest') || message.includes('manifest') || message.includes('webmanifest'))) {
      return true;
    }
    
    // Filter generic 404 errors for known resources (fonts, manifest, etc.)
    if (message.includes('Failed to load resource') && 
        message.includes('responded with a status of 404') &&
        (message.includes('woff') || message.includes('woff2') || 
         message.includes('manifest') || message.includes('webmanifest') ||
         message.includes('fonts.gstatic.com') || message.includes('gstatic'))) {
      return true;
    }
    
    // Filter AdFooter success messages (informational, not errors)
    if (message.includes('[AdFooter]') && 
        (message.includes('All checks passed') || message.includes('loading ad') || message.includes('✅'))) {
      return true;
    }
    
    // Filter generic 404 errors (including empty parentheses format)
    // Catch "Failed to load resource: the server responded with a status of 404 ()"
    if ((message.includes('Failed to load resource') || message.includes('responded with a status of 404')) &&
        (message.includes('404') || message.includes('()') || message.trim().endsWith('()'))) {
      return true;
    }
    
    // Filter empty 404 errors (just "Failed to load resource: the server responded with a status of 404 ()")
    // This catches generic 404 errors without specific resource names
    if (message.includes('Failed to load resource') && 
        message.includes('responded with a status of 404')) {
      // Filter if it ends with empty parentheses (generic 404) or is a known resource type
      if (message.includes('()') || 
          message.trim().endsWith('()') ||
          message.includes('woff') || 
          message.includes('manifest') || 
          message.includes('gstatic') ||
          message.includes('payment') ||
          message.includes('pricing') ||
          message.includes('2fa') ||
          // Generic 404 without resource name - filter it
          (message.includes('404') && !message.match(/:\d+.*Failed/))) {
        return true;
      }
    }
    
    // Filter network errors (ERR_ABORTED 404) for fonts
    if ((message.includes('ERR_ABORTED') || message.includes('net::ERR_ABORTED')) &&
        (message.includes('404') || message.includes('Not Found')) &&
        (message.includes('woff') || message.includes('woff2') || 
         message.includes('fonts.gstatic.com') || message.includes('gstatic') ||
         message.includes('UcCO3FwrK3iLTeHuS'))) {
      return true;
    }
    
    // Filter GET requests that fail for fonts
    if (message.includes('GET') && 
        (message.includes('fonts.gstatic.com') || message.includes('gstatic')) &&
        (message.includes('404') || message.includes('ERR_ABORTED') || message.includes('Not Found'))) {
      return true;
    }
    
    // Filter specific font file errors
    if (message.includes('UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiJ-Ek-_EeA.woff2') &&
        (message.includes('Failed') || message.includes('404') || message.includes('ERR_ABORTED'))) {
      return true;
    }
    
    return false;
  }

  // Override console.warn
  console.warn = (...args: any[]) => {
    const message = args.join(' ');
    
    if (!shouldFilterMessage(message)) {
      originalWarn.apply(console, args);
    }
  };

  // Override console.error for storage-related errors from third-party scripts
  console.error = (...args: any[]) => {
    const message = args.join(' ');
    
    if (!shouldFilterMessage(message)) {
      originalError.apply(console, args);
    }
  };

  // Also intercept console.log to filter informational messages
  const originalLog = console.log;
  console.log = (...args: any[]) => {
    const message = args.join(' ');
    
    // Filter AdFooter success messages and other informational logs
    if (shouldFilterMessage(message) || 
        message.includes('[AdFooter]') ||
        message.includes('All checks passed')) {
      return; // Don't log filtered messages
    }
    
    originalLog.apply(console, args);
  };

  // Return cleanup function
  return () => {
    console.warn = originalWarn;
    console.error = originalError;
  };
};

/**
 * Initialize console filter
 * Filters known third-party warnings that don't affect functionality
 */
export const initConsoleFilter = (): void => {
  // Filter in both production and development to reduce console noise
  // These are expected browser warnings from third-party scripts (AdSense, Analytics)
  // and don't indicate actual errors in our application
  setupConsoleFilter();
};

